import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowDown, ArrowLeft, ArrowUp, Minus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import {
  type AnalyticsPeriod,
  type AnalyticsPeriodBlock,
  getSellerAnalytics,
  type SellerAnalytics,
} from '@/api/analytics'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { describeErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/seller/analytics')({
  component: AnalyticsPage,
})

const PERIOD_LABEL: Record<AnalyticsPeriod, string> = {
  week: 'Неделя',
  month: 'Месяц',
}

function AnalyticsPage() {
  const navigate = useNavigate()
  const [period, setPeriod] = useState<AnalyticsPeriod>('week')

  const query = useQuery({
    // Период входит в ключ кэша — TanStack Query переключает payload'ы
    // без лишнего запроса при возврате на уже виденный период.
    queryKey: ['analytics', period],
    queryFn: () => getSellerAnalytics(period),
  })

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-6 pb-12">
      <Button
        type="button"
        variant="ghost"
        className="-ml-3 w-fit gap-2 text-muted-foreground"
        onClick={() => navigate({ to: '/seller/dashboard' })}
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />К списку записей
      </Button>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Моя динамика</h1>
          <p className="text-sm text-muted-foreground">
            Количество записей, средний балл и тренды по тегам за период.
          </p>
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </header>

      {query.isLoading ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState message={describeErrorMessage(query.error)} />
      ) : query.data ? (
        <AnalyticsBody analytics={query.data} />
      ) : null}
    </main>
  )
}

interface PeriodToggleProps {
  value: AnalyticsPeriod
  onChange: (next: AnalyticsPeriod) => void
}

function PeriodToggle({ value, onChange }: PeriodToggleProps) {
  return (
    <div
      role="tablist"
      aria-label="Период аналитики"
      className="inline-flex items-center rounded-lg border bg-muted p-1"
    >
      {(['week', 'month'] as AnalyticsPeriod[]).map((kind) => {
        const active = kind === value
        return (
          <button
            key={kind}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(kind)}
            className={cn(
              'min-h-10 rounded-md px-4 py-1.5 text-sm font-medium transition-colors sm:min-h-8',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {PERIOD_LABEL[kind]}
          </button>
        )
      })}
    </div>
  )
}

interface AnalyticsBodyProps {
  analytics: SellerAnalytics
}

function AnalyticsBody({ analytics }: AnalyticsBodyProps) {
  const topTagCurrent = analytics.current.tagDistribution[0]
  const topTagPrevious = analytics.previous.tagDistribution.find(
    (t) => t.code === topTagCurrent?.code,
  )

  // Для «средний балл» и «кол-во» сравниваем current ↔ previous одного типа
  // (plan §6.3: дельты знаками зелёный/красный).
  const countDelta = analytics.current.consultationsCount - analytics.previous.consultationsCount
  const scoreDelta = diffOrNull(
    analytics.current.avgScorePercent,
    analytics.previous.avgScorePercent,
  )
  const topTagDelta =
    topTagCurrent && topTagPrevious ? topTagCurrent.count - topTagPrevious.count : null

  return (
    <div className="flex flex-col gap-4">
      {/* Три KPI-карточки: на mobile идут столбцом, на sm+ в ряд (plan §6.5). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard
          title="Консультаций"
          value={analytics.current.consultationsCount.toString()}
          delta={countDelta}
          deltaSuffix=""
          hint={`За прошлый период: ${analytics.previous.consultationsCount}`}
          emptyHint="Нет консультаций за период"
        />
        <KpiCard
          title="Средний балл"
          value={formatScore(analytics.current.avgScorePercent)}
          delta={scoreDelta}
          deltaSuffix="%"
          hint={
            analytics.previous.avgScorePercent === null
              ? 'За прошлый период нет данных'
              : `За прошлый период: ${formatScore(analytics.previous.avgScorePercent)}`
          }
          emptyHint={
            analytics.current.avgScorePercent === null ? 'Пока нет обработанных записей' : undefined
          }
        />
        <KpiCard
          title="Топ-тег"
          value={topTagCurrent ? topTagCurrent.label : '—'}
          delta={topTagDelta}
          deltaSuffix=""
          hint={
            topTagCurrent ? `Встретился ${topTagCurrent.count} раз` : 'Пока нет тегов за период'
          }
        />
      </div>

      <TagDistributionCard current={analytics.current} />
      <AggregatedRecommendationsCard bullets={analytics.aggregatedRecommendations} />
    </div>
  )
}

interface KpiCardProps {
  title: string
  value: string
  delta: number | null
  deltaSuffix: string
  hint: string
  emptyHint?: string
}

function KpiCard({ title, value, delta, deltaSuffix, hint, emptyHint }: KpiCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <div className="flex items-baseline gap-2">
          <CardTitle className="text-3xl leading-tight">{value}</CardTitle>
          {delta !== null ? <DeltaBadge delta={delta} suffix={deltaSuffix} /> : null}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-xs text-muted-foreground">{emptyHint ?? hint}</p>
      </CardContent>
    </Card>
  )
}

interface DeltaBadgeProps {
  delta: number
  suffix: string
}

// DeltaBadge — положительная дельта зелёная, отрицательная красная, ноль
// нейтральный (цвет фиксирует подсознательное «хорошо/плохо»). Стрелка
// задаёт направление, подпись даёт точное значение.
function DeltaBadge({ delta, suffix }: DeltaBadgeProps) {
  const rounded = Math.round(delta * 10) / 10
  if (rounded === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
        role="status"
        aria-label="Без изменений"
      >
        <Minus className="h-3 w-3" aria-hidden="true" />0{suffix}
      </span>
    )
  }
  const positive = rounded > 0
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        positive ? 'bg-emerald-500/10 text-emerald-600' : 'bg-destructive/10 text-destructive',
      )}
      role="status"
      aria-label={`Изменение: ${positive ? '+' : ''}${rounded}${suffix}`}
    >
      {positive ? (
        <ArrowUp className="h-3 w-3" aria-hidden="true" />
      ) : (
        <ArrowDown className="h-3 w-3" aria-hidden="true" />
      )}
      {positive ? '+' : ''}
      {rounded}
      {suffix}
    </span>
  )
}

interface TagDistributionCardProps {
  current: AnalyticsPeriodBlock
}

function TagDistributionCard({ current }: TagDistributionCardProps) {
  const data = useMemo(
    () =>
      current.tagDistribution.map((t) => ({
        label: t.label,
        count: t.count,
      })),
    [current.tagDistribution],
  )

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Распределение тегов</CardTitle>
          <CardDescription>Пока нет размеченных консультаций за период.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Распределение тегов</CardTitle>
        <CardDescription>Сколько раз каждый тег встретился в ваших записях.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full" role="img" aria-label="Распределение тегов">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 24 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="color-mix(in oklch, var(--color-muted-foreground) 15%, transparent)"
              />
              <XAxis
                dataKey="label"
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                interval={0}
                angle={-20}
                dy={12}
                height={60}
                textAnchor="end"
              />
              <YAxis allowDecimals={false} stroke="var(--color-muted-foreground)" fontSize={11} />
              <Tooltip
                cursor={{ fill: 'color-mix(in oklch, var(--color-muted) 50%, transparent)' }}
                contentStyle={{
                  background: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Bar
                dataKey="count"
                fill="var(--color-primary)"
                radius={[6, 6, 0, 0]}
                maxBarSize={48}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

interface AggregatedRecommendationsCardProps {
  bullets: string[]
}

function AggregatedRecommendationsCard({ bullets }: AggregatedRecommendationsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Зоны роста за период</CardTitle>
        <CardDescription>
          {bullets.length > 0
            ? 'Сформированы на основе последних консультаций.'
            : 'Появятся, когда за период будет хотя бы три обработанные записи.'}
        </CardDescription>
      </CardHeader>
      {bullets.length > 0 ? (
        <CardContent>
          <ul className="flex list-disc flex-col gap-2 pl-5 text-sm text-foreground">
            {bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </CardContent>
      ) : null}
    </Card>
  )
}

function LoadingState() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Загружаем аналитику</CardTitle>
        <CardDescription>Это займёт секунду.</CardDescription>
      </CardHeader>
    </Card>
  )
}

function ErrorState({ message }: { message?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Не удалось загрузить аналитику</CardTitle>
        <CardDescription>{message ?? 'Попробуйте обновить страницу.'}</CardDescription>
      </CardHeader>
    </Card>
  )
}

function formatScore(value: number | null): string {
  if (value === null) return '—'
  return `${Math.round(value)}%`
}

function diffOrNull(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null
  return current - previous
}
